import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ImageUpload from '../../components/ImageUpload';

const SpeakerProfilePage = () => {
  const { speakerProfile, setSpeakerProfile } = useOutletContext();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [newTopic, setNewTopic] = useState('');
  const [form, setForm] = useState({
    name: speakerProfile?.name || '',
    title: speakerProfile?.title || '',
    email: speakerProfile?.email || '',
    church_name: speakerProfile?.church_name || '',
    church_city: speakerProfile?.church_city || '',
    church_state: speakerProfile?.church_state || '',
    bio: speakerProfile?.bio || '',
    photo_url: speakerProfile?.photo_url || '',
    website_url: speakerProfile?.website_url || '',
    facebook_url: speakerProfile?.facebook_url || '',
    instagram_url: speakerProfile?.instagram_url || '',
    twitter_url: speakerProfile?.twitter_url || '',
    youtube_url: speakerProfile?.youtube_url || '',
    booking_url: speakerProfile?.booking_url || '',
    book_title: speakerProfile?.book_title || '',
    book_url: speakerProfile?.book_url || '',
    amazon_author_url: speakerProfile?.amazon_author_url || '',
    youtube_video_id: speakerProfile?.youtube_video_id || '',
    is_available: speakerProfile?.is_available !== false,
    topics: speakerProfile?.topics || []
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('speakers')
        .update(form)
        .eq('id', speakerProfile.id);
      if (error) throw error;
      setSpeakerProfile({ ...speakerProfile, ...form });
      showToast('Profile saved successfully!');
    } catch (error) {
      showToast('Failed to save: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const addTopic = () => {
    if (newTopic.trim() && !form.topics.includes(newTopic.trim())) {
      setForm({ ...form, topics: [...form.topics, newTopic.trim()] });
      setNewTopic('');
    }
  };

  const removeTopic = (topic) => {
    setForm({ ...form, topics: form.topics.filter(t => t !== topic) });
  };

  const getCompletion = () => {
    const fields = [
      { key: 'name', value: form.name },
      { key: 'title', value: form.title },
      { key: 'church_name', value: form.church_name },
      { key: 'bio', value: form.bio },
      { key: 'photo_url', value: form.photo_url },
      { key: 'website_url', value: form.website_url },
      { key: 'topics', value: form.topics?.length > 0 },
    ];
    const completed = fields.filter(f => f.value).length;
    return Math.round((completed / fields.length) * 100);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-lg ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Profile</h1>
          <p className="mt-1 text-sm text-gray-500">Update your speaker profile information.</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Profile Completion</p>
          <p className="text-2xl font-bold text-indigo-600">{getCompletion()}%</p>
        </div>
      </div>

      {/* Photo */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profile Photo</h2>
        <ImageUpload
          bucket="speakers"
          currentImage={form.photo_url}
          onUpload={(url) => setForm({ ...form, photo_url: url })}
        />
      </div>

      {/* Basic Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name *</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title/Position</label>
            <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="e.g., Senior Pastor" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
            <input type="url" value={form.website_url} onChange={e => setForm({...form, website_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Biography</label>
          <textarea rows={4} value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="Tell event planners about yourself..." />
        </div>
      </div>

      {/* Church Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Church Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Church Name</label>
            <input type="text" value={form.church_name} onChange={e => setForm({...form, church_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
            <input type="text" value={form.church_city} onChange={e => setForm({...form, church_city: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
            <input type="text" value={form.church_state} onChange={e => setForm({...form, church_state: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
        </div>
      </div>

      {/* Speaking Topics */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Speaking Topics</h2>
        <div className="flex gap-2 mb-4">
          <input type="text" value={newTopic} onChange={e => setNewTopic(e.target.value)} onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTopic())} placeholder="Add a topic..." className="flex-1 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          <button onClick={addTopic} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.topics.map((topic, i) => (
            <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
              {topic}
              <button onClick={() => removeTopic(topic)} className="ml-2 hover:text-red-500">×</button>
            </span>
          ))}
          {form.topics.length === 0 && <p className="text-gray-400 text-sm">No topics added yet</p>}
        </div>
      </div>

      {/* Featured Video */}
      <div className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-xl shadow-sm border border-red-100 dark:border-red-800 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Featured Video</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">YouTube Video ID</label>
          <input 
            type="text" 
            value={form.youtube_video_id} 
            onChange={e => setForm({...form, youtube_video_id: e.target.value})} 
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" 
            placeholder="e.g., dQw4w9WgXcQ"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter the YouTube video ID from the URL. For example, if the URL is <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">https://youtube.com/watch?v=dQw4w9WgXcQ</code>, enter <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">dQw4w9WgXcQ</code>
          </p>
        </div>
        {form.youtube_video_id && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Video Preview:</p>
            <div className="aspect-video bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${form.youtube_video_id}`}
                title="YouTube video preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        )}
      </div>

      {/* Social Links */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Social Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Facebook</label>
            <input type="url" value={form.facebook_url} onChange={e => setForm({...form, facebook_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://facebook.com/..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instagram</label>
            <input type="url" value={form.instagram_url} onChange={e => setForm({...form, instagram_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://instagram.com/..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">X (Twitter)</label>
            <input type="url" value={form.twitter_url} onChange={e => setForm({...form, twitter_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://x.com/..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">YouTube Channel</label>
            <input type="url" value={form.youtube_url} onChange={e => setForm({...form, youtube_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://youtube.com/..." />
          </div>
        </div>
      </div>

      {/* Booking */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl shadow-sm border border-purple-100 dark:border-purple-800 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Booking Information</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Booking Page URL</label>
          <input type="url" value={form.booking_url} onChange={e => setForm({...form, booking_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://calendly.com/..." />
          <p className="text-xs text-gray-500 mt-1">Link to Calendly, Acuity, or your booking page</p>
        </div>
        <div className="mt-4 flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Available for Booking</p>
            <p className="text-sm text-gray-500">Show as available on your public profile</p>
          </div>
          <button
            onClick={() => setForm({...form, is_available: !form.is_available})}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${form.is_available ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${form.is_available ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Books */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl shadow-sm border border-amber-100 dark:border-amber-800 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Books & Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Book Title</label>
            <input type="text" value={form.book_title} onChange={e => setForm({...form, book_title: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Book Purchase URL</label>
            <input type="url" value={form.book_url} onChange={e => setForm({...form, book_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amazon Author Page</label>
          <input type="url" value={form.amazon_author_url} onChange={e => setForm({...form, amazon_author_url: e.target.value})} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" placeholder="https://amazon.com/author/..." />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default SpeakerProfilePage;
